import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ErrorBoundary } from "react-error-boundary";
import { z } from "zod";

// import { useLocalStorage } from "@uidotdev/usehooks";

import { SidebarProvider } from "@/components/ui/sidebar/sidebar";
import { ProtectedProvider } from "@/features/auth/_providers/protected-provider";
import { uploadFromDirectorySearchFragment } from "@/features/office/_components/file-library/file-library-search-fragment";
import { CheckRoleProvider } from "@/features/office/_providers/check-role-provider";
import { PrefetchProvider } from "@/features/office/_providers/prefetch-provider";
import { GlobalSearch } from "@/features/office/global-search";
import { CreateOrderDialog } from "@/features/office/orders/create";
import { CreatePlaceDialog } from "@/features/office/places/create";
import { EditPlaceDialog } from "@/features/office/places/edit";
import { AppSidebar } from "@/features/office/sidebar";
import { SmartActionShadowDialog } from "@/features/office/smart-actions/shadow";
import { CreateTaskDialog } from "@/features/office/tasks/create";
import { CreateVendorDialog } from "@/features/office/vendors/create";
import { ErrorFallback } from "@/features/shared/error-fallback";
import { GlobalBirdieChat } from "@/features/shared/global-birdie-chat";
import { BirdieAwareSidebarInset } from "@/features/shared/global-birdie-chat/_components/birdie-aware-sidebar-inset";
import { PageContextRoot } from "@/features/shared/page-context";
import { StatsigProvider } from "@/features/shared/providers/statsig-provider";
import {
  TeamRealtimeProvider,
  UserRealtimeProvider,
} from "@/features/shared/realtime-refresh";
import { maskSearchFragment } from "@/features/shared/route-masking";
import { useListenHotkeyEvent } from "@/hooks/use-listen-hotkey-event";
import { useQueryState } from "@/hooks/use-query-state";

// import { useScreen } from "@/hooks/use-screen";

export const Route = createFileRoute("/office")({
  component: RouteComponent,
  // The Upload from directory picker opens over many office screens; declaring
  // its keys here gives each of them the parsed filters (a child's schema keeps
  // the parent's keys).
  validateSearch: z.object({
    ...uploadFromDirectorySearchFragment,
    ...maskSearchFragment,
  }),
});

function RouteComponent() {
  // const isDefaultOpen = useScreen({ base: false, "2xl": true });
  // const [defaultOpen] = useLocalStorage("sidebar_open", isDefaultOpen);
  const [action] = useQueryState<string>("action");
  useListenHotkeyEvent();

  return (
    <ProtectedProvider>
      <StatsigProvider>
        <CheckRoleProvider>
          <TeamRealtimeProvider>
            <UserRealtimeProvider>
              <PrefetchProvider>
                <PageContextRoot>
                  <SidebarProvider>
                    <AppSidebar />
                    <ErrorBoundary
                      onError={(error, info) => {
                        Sentry.captureException(error, {
                          tags: { boundary: "office" },
                          contexts: {
                            react: { componentStack: info.componentStack },
                          },
                        });
                      }}
                      fallbackRender={({ error }) => (
                        <ErrorFallback error={error} />
                      )}
                    >
                      <BirdieAwareSidebarInset>
                        <Outlet />
                      </BirdieAwareSidebarInset>
                    </ErrorBoundary>
                  </SidebarProvider>
                  {/* Global Dialog */}
                  {action === "create-order" && <CreateOrderDialog />}
                  {action === "create-vendor" && <CreateVendorDialog />}
                  {action === "create-place" && <CreatePlaceDialog />}
                  {action === "edit-place" && <EditPlaceDialog />}
                  {action === "global-search" && <GlobalSearch />}
                  {action === "create-task" && <CreateTaskDialog />}
                  {/* `?smartActionId=` on any office page: the popup over it (SA-D33) */}
                  <SmartActionShadowDialog />
                  <GlobalBirdieChat />
                  {/* End Global Dialog */}
                </PageContextRoot>
              </PrefetchProvider>
            </UserRealtimeProvider>
          </TeamRealtimeProvider>
        </CheckRoleProvider>
      </StatsigProvider>
    </ProtectedProvider>
  );
}
